import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Badge, Collapse } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaDownload, FaQrcode } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { downloadAuthenticatedFile } from '../../../store/orderSlice';

const TrackingLogsModal = ({ isOpen, onClose, trackingLogs = [] }) => {
    const dispatch = useDispatch();
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [labelDownloading, setLabelDownloading] = useState(null);
    const [qrDownloading, setQrDownloading] = useState(null);

    const handleFileDownload = (url, filename, setLoading, key) => {
        if (setLoading === setLabelDownloading && labelDownloading === key) return;
        if (setLoading === setQrDownloading && qrDownloading === key) return;
        setLoading(key);
        dispatch(downloadAuthenticatedFile(url)).then((res) => {
            if (res.error) {
                toast.error(res.payload?.error || "Failed to download file", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } else {
                const blob = new Blob([res.payload]);
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
            setLoading(null);
        }).catch(() => {
            toast.error("Failed to download file", {
                position: toast.POSITION.TOP_RIGHT,
            });
            setLoading(null);
        });
    };

    const toggleExpand = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        if (!status) return <Badge color="secondary">No Status</Badge>;
        return <Badge color="info">{status}</Badge>;
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" className="tracking-logs-modal">
            <ModalHeader toggle={onClose}>
                Tracking Logs
            </ModalHeader>
            <ModalBody className="tracking-logs-body">
                {trackingLogs && trackingLogs.length > 0 ? (
                    <div className="tracking-logs-container">
                        {trackingLogs.map((log, index) => (
                            <div key={index} className="tracking-log-item mb-3 border rounded p-3">
                                {/* Main Tracking Info */}
                                <div 
                                    className="d-flex justify-content-between align-items-center mb-2 cursor-pointer"
                                    onClick={() => toggleExpand(index)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <strong>
                                                {log.tracking.tracking_name} - {log.tracking.tracking_number || 'N/A'}
                                            </strong>
                                            {log.tracking.deleted_at ? (
                                                <Badge className='ml-2' color="danger">Deleted</Badge>
                                            ) : (
                                                <Badge className='ml-2' color="success">Active</Badge>
                                            )}
                                        </div>
                                        <small className="text-muted d-block mt-1">
                                            Created: {formatDate(log.tracking.created_at)} | 
                                            Updated: {formatDate(log.tracking.updated_at)}
                                        </small>
                                    </div>
                                    <div className="ml-3">
                                        {expandedIndex === index ? (
                                            <FaChevronUp size={16} />
                                        ) : (
                                            <FaChevronDown size={16} />
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                <Collapse isOpen={expandedIndex === index}>
                                    <hr className="my-2" />
                                    
                                    {/* Tracking Details */}
                                    <div className="mt-3">
                                        <h6 className="mb-2">
                                            <strong>Tracking Details</strong>
                                        </h6>
                                        <div className="ml-3">
                                            <div className="mb-2">
                                                <strong>Tracking ID:</strong>{' '}
                                                <span>{log.tracking.tracking_id}</span>
                                            </div>
                                            <div className="mb-2">
                                                <strong>Tracking Number:</strong>{' '}
                                                <span>{log.tracking.tracking_number || '-'}</span>
                                            </div>
                                            <div className="mb-2">
                                                <strong>Carrier:</strong>{' '}
                                                <span>{log.tracking.tracking_name}</span>
                                            </div>
                                            <div className="mb-2">
                                                <strong>Tracking URL:</strong>{' '}
                                                {log.tracking.tracking_url ? (
                                                    <a 
                                                        href={log.tracking.tracking_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-primary"
                                                    >
                                                        View Tracking
                                                    </a>
                                                ) : (
                                                    <span>-</span>
                                                )}
                                            </div>
                                            {log.tracking.error_message && (
                                                <div className="mb-2">
                                                    <strong>Error Message:</strong>{' '}
                                                    <span className="text-danger font-italic">
                                                        {log.tracking.error_message}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Download Links — only when a label or QR is actually available
                                        (both null for Fynd/India returns, so the section is hidden). */}
                                    {(log.tracking_label_url || log.tracking_qrcode_url) && (
                                    <div className="mt-3 mb-3">
                                        <h6 className="mb-2">
                                            <strong>Downloads</strong>
                                        </h6>
                                        <div className="ml-3">
                                            {log.tracking_label_url && (
                                                <Button
                                                    color="info"
                                                    size="sm"
                                                    className="mr-2 mb-2"
                                                    disabled={labelDownloading === index}
                                                    onClick={() => handleFileDownload(log.tracking_label_url, `return-label-${index}.pdf`, setLabelDownloading, index)}
                                                >
                                                    {labelDownloading === index ? <i className="fa fa-spinner fa-spin mr-1" /> : <FaDownload className="mr-1" />} Return Label
                                                </Button>
                                            )}
                                            {log.tracking_qrcode_url && (
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    className="mb-2"
                                                    disabled={qrDownloading === index}
                                                    onClick={() => handleFileDownload(log.tracking_qrcode_url, `qr-code-${index}.png`, setQrDownloading, index)}
                                                >
                                                    {qrDownloading === index ? <i className="fa fa-spinner fa-spin mr-1" /> : <FaQrcode className="mr-1" />} QR Code
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    {/* Tracking Summary/Events */}
                                    {log.tracking_summary && log.tracking_summary.events && log.tracking_summary.events.length > 0 && (
                                        <div className="mt-3">
                                            <h6 className="mb-2">
                                                <strong>Tracking Events</strong>
                                            </h6>
                                            <div className="ml-3">
                                                <div className="table-responsive">
                                                    <Table size="sm" className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ fontSize: '0.85rem' }}>
                                                                    Date/Time
                                                                </th>
                                                                <th style={{ fontSize: '0.85rem' }}>
                                                                    Event
                                                                </th>
                                                                <th style={{ fontSize: '0.85rem' }}>
                                                                    Location
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {log.tracking_summary.events.map((event, eventIndex) => (
                                                                <tr key={eventIndex}>
                                                                    <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                                                        {event.event_date_time}
                                                                    </td>
                                                                    <td style={{ fontSize: '0.85rem' }}>
                                                                        {event.event_name}
                                                                    </td>
                                                                    <td style={{ fontSize: '0.85rem' }}>
                                                                        {event.location_name || '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {(!log.tracking_summary || !log.tracking_summary.events || log.tracking_summary.events.length === 0) && (
                                        <div className="mt-3 alert alert-info" style={{ marginBottom: 0 }}>
                                            No tracking events available
                                        </div>
                                    )}

                                    {/* Metadata */}
                                    <div className="mt-3 pt-2 border-top">
                                        <small className="text-muted">
                                            <strong>Last Sync:</strong>{' '}
                                            {log.tracking.last_sync_at ? formatDate(log.tracking.last_sync_at) : 'Never'} | 
                                            <strong className="ml-2">Sync Errors:</strong> {log.tracking.sync_error_count}
                                        </small>
                                    </div>
                                </Collapse>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="alert alert-info">
                        No tracking logs available
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default TrackingLogsModal;
